/**
 * @file AnalyticsViewPicker.tsx
 * @brief Searchable "view" selector for Company Analytics.
 *
 * Replaces the plain dropdown so it scales to 100s of employees: the same
 * searchable PickerSheet the language / user pickers use. Options are
 * "All employees" (whole company), "Me" (the signed-in admin), and every other
 * employee — searchable by name or email. (15-07-2026)
 */

import { useMemo, useState } from "react";
import { ChevronsUpDown, User as UserIcon, Users } from "lucide-react";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { Avatar } from "@/components/ui/Avatar";
import { cn } from "@/shared/theme/cn";
import { formatFullName, getInitials } from "@/shared/utils/formatters";
import type { CompanyEmployee } from "@/features/employees/schemas";

// Sentinel option values (real employees use their UUID).
export const VIEW_ALL = "company";
export const VIEW_ME = "personal";

interface ViewItem extends PickerSheetItem {
  employee?: CompanyEmployee;
}

interface AnalyticsViewPickerProps {
  /** "company" (all) | "personal" (me) | an employee UUID. */
  value: string;
  onChange: (next: string) => void;
  /** Employees to offer, already excluding the signed-in admin (covered by "Me"). */
  employees: CompanyEmployee[];
  loading?: boolean;
}

export function AnalyticsViewPicker({
  value,
  onChange,
  employees,
  loading,
}: AnalyticsViewPickerProps) {
  const [open, setOpen] = useState(false);

  const items: ViewItem[] = useMemo(() => {
    const pinned: ViewItem[] = [
      {
        id: VIEW_ALL,
        label: "All employees",
        subtitle: "Whole company",
        searchKeys: ["all", "company", "everyone", "team"],
      },
      {
        id: VIEW_ME,
        label: "Me",
        subtitle: "Just my own activity",
        searchKeys: ["me", "personal", "self", "mine"],
      },
    ];
    const emps: ViewItem[] = employees.map((e) => {
      const name = formatFullName(e.first_name, e.last_name);
      return {
        id: e.id,
        employee: e,
        label: name !== "—" ? name : e.email,
        subtitle: e.email,
        searchKeys: [e.email, e.first_name, e.last_name],
      };
    });
    return [...pinned, ...emps];
  }, [employees]);

  const selected = items.find((i) => i.id === value);
  const triggerLabel = selected?.label ?? "All employees";

  return (
    <>
      <PickerSheet<ViewItem>
        anchor={
          <button
            type="button"
            disabled={loading}
            className={cn(
              "inline-flex h-10 w-[260px] items-center justify-between gap-2 rounded-full border border-input bg-field px-5 text-sm transition-colors",
              "hover:bg-muted/50 disabled:opacity-50",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-[10px] font-bold text-brand">
                {selected?.employee ? (
                  getInitials(
                    selected.employee.first_name,
                    selected.employee.last_name,
                    selected.employee.email,
                  )
                ) : value === VIEW_ALL ? (
                  <Users className="h-4 w-4" />
                ) : (
                  <UserIcon className="h-4 w-4" />
                )}
              </span>
              <span className="truncate">{triggerLabel}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        }
        open={open}
        onOpenChange={setOpen}
        title="View analytics for"
        searchPlaceholder="Search employees…"
        items={items}
        selectedId={value}
        onSelect={(item) => onChange(item.id)}
        loading={loading}
        renderLeftAccessory={(item) =>
          item.employee ? (
            <Avatar
              src={item.employee.profile_image_url}
              initials={getInitials(
                item.employee.first_name,
                item.employee.last_name,
                item.employee.email,
              )}
              gender={item.employee.gender}
              alt={item.label}
              size="xl"
            />
          ) : (
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand">
              {item.id === VIEW_ALL ? (
                <Users className="h-6 w-6" aria-hidden />
              ) : (
                <UserIcon className="h-6 w-6" aria-hidden />
              )}
            </span>
          )
        }
      />
    </>
  );
}
